/**
 * BlogContentEditor - Editor de contenido para posts del blog
 * 
 * ✨ NUEVA FUNCIONALIDAD: Drag and Drop para reordenar contenido
 * 
 * Características:
 * - Drag handle (⋮⋮) en cada bloque para reordenar
 * - Botones Up/Down reemplazados por drag and drop intuitivo
 * - Mantiene botón Remove para eliminar bloques
 * - Soporte completo para teclado y accesibilidad
 * - Estados visuales para feedback del usuario
 * 
 * Tecnologías: @dnd-kit/core, @dnd-kit/sortable
 */

import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BlogContentEditor = forwardRef(({
  content,
  onChange,
  onImagesChange,
  existingContentImages
}, ref) => {
  const [contentImages, setContentImages] = useState([]);
  const [blockIds, setBlockIds] = useState([]); // IDs únicos y estables
  
  // Ref del contenedor scrollable de los bloques
  const blocksContainerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const normalizeBlocks = () => {
    if (Array.isArray(content)) return content;
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        if (content) return [{ type: 'text', content }];
      }
    }
    return [];
  };

  // Generar IDs únicos para los bloques
  const generateBlockIds = (blocks) => {
    if (blockIds.length === 0 || blockIds.length !== blocks.length) {
      const newIds = blocks.map((_, index) => `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
      setBlockIds(newIds);
      return newIds;
    }
    return blockIds;
  };

  useEffect(() => {
    const blocks = normalizeBlocks();
    const imageBlocks = blocks.filter(b => b && b.type === 'image');
    if (imageBlocks.length > 0 && contentImages.length === 0) {
      const placeholderImages = imageBlocks.map(block => ({
        id: block.image_id,
        file: null,
        caption: block.caption || 'Image',
        originalName: 'Existing Image'
      }));
      setContentImages(placeholderImages);
      if (onImagesChange) onImagesChange(placeholderImages);
    }
    
    // Generar IDs únicos para los bloques existentes
    if (blocks.length > 0) {
      generateBlockIds(blocks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const updateBlocks = (newBlocks) => {
    onChange(Array.isArray(newBlocks) ? newBlocks : []);
  };

  // Helper: hace scroll al fondo del contenedor de bloques si existe
  const scrollToBottom = () => {
    const container = blocksContainerRef.current;
    if (!container) return;
    // Usar requestAnimationFrame para esperar el layout
    requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    });
  };

  const handleTextBlockChange = (index, value) => {
    const blocks = normalizeBlocks();
    const updated = blocks.map((b, i) => i === index ? { ...b, content: value } : b);
    updateBlocks(updated);
  };

  const createTextBlock = () => ({ type: 'text', content: '' });

  const pickImageFile = (cb) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) cb(file);
    };
    input.click();
  };

  const insertTextAtEnd = () => {
    const blocks = normalizeBlocks();
    const newBlock = createTextBlock();
    const newBlocks = [...blocks, newBlock];
    
    // Agregar nuevo ID para el nuevo bloque
    const newId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setBlockIds([...blockIds, newId]);
    
    updateBlocks(newBlocks);

    // Desplazar al fondo para mostrar el nuevo párrafo
    scrollToBottom();
  };

  const insertImageAtEnd = (file) => {
    const blocks = normalizeBlocks();
    const imageId = `temp_${file.name}`;
    const imageBlock = { type: 'image', image_id: imageId, caption: file.name };
    const newBlocks = [...blocks, imageBlock];
    
    // Agregar nuevo ID para el nuevo bloque
    const newId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setBlockIds([...blockIds, newId]);
    
    updateBlocks(newBlocks);

    const newImage = { id: imageId, file, caption: file.name, originalName: file.name };
    const updatedImages = [...contentImages, newImage];
    setContentImages(updatedImages);
    if (onImagesChange) onImagesChange(updatedImages);

    // Desplazar al fondo para mostrar la nueva imagen
    scrollToBottom();
  };

  const handleRemoveBlock = (index, block) => {
    const blocks = normalizeBlocks();
    const newBlocks = blocks.filter((_, i) => i !== index);
    
    // Remover el ID correspondiente
    const newBlockIds = blockIds.filter((_, i) => i !== index);
    setBlockIds(newBlockIds);
    
    updateBlocks(newBlocks);

    if (block.type === 'image') {
      const updatedImages = contentImages.filter(img => img.id !== block.image_id);
      setContentImages(updatedImages);
      if (onImagesChange) onImagesChange(updatedImages);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    console.log('🔍 Drag End Event:', { active: active.id, over: over.id });

    if (active.id !== over.id) {
      const blocks = normalizeBlocks();
      
      // Encontrar los índices usando los IDs únicos
      const activeIndex = blockIds.indexOf(active.id);
      const overIndex = blockIds.indexOf(over.id);
      
      console.log('🔍 Indices encontrados:', { activeIndex, overIndex, blocksLength: blocks.length, blockIds });
      
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        console.log('🔍 Moviendo bloque de', activeIndex, 'a', overIndex);
        
        // Reordenar los bloques
        const newBlocks = arrayMove(blocks, activeIndex, overIndex);
        
        // Reordenar los IDs también
        const newBlockIds = arrayMove(blockIds, activeIndex, overIndex);
        setBlockIds(newBlockIds);
        
        // Actualizar el contenido
        updateBlocks(newBlocks);
        
        // Feedback visual sutil
        const draggedBlock = document.querySelector(`[data-id="${active.id}"]`);
        if (draggedBlock) {
          draggedBlock.style.transition = 'all 0.3s ease';
          setTimeout(() => {
            if (draggedBlock) {
              draggedBlock.style.transition = '';
            }
          }, 300);
        }
      } else {
        console.log('🔍 Índices inválidos o iguales:', { activeIndex, overIndex });
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getContentImages: () => contentImages.filter(img => img.file instanceof File).map(img => img.file),
    clearContentImages: () => {
      setContentImages([]);
      if (onImagesChange) onImagesChange([]);
    }
  }));

  const blocks = normalizeBlocks();
  if (blocks.length === 0) {
    blocks.push(createTextBlock());
    updateBlocks(blocks);
  }

  const resolveImageSrc = (block, imageOrderIndex) => {
    const localMatch = contentImages.find(img => img.id === block.image_id && img.file instanceof File);
    if (localMatch) return URL.createObjectURL(localMatch.file);

    const byId = Array.isArray(existingContentImages)
      ? existingContentImages.find(ci => String(ci.id) === String(block.image_id))
      : null;
    if (byId && byId.image_url) return byId.image_url;

    if (Array.isArray(existingContentImages) && existingContentImages[imageOrderIndex]) {
      return existingContentImages[imageOrderIndex].image_url || existingContentImages[imageOrderIndex].image;
    }

    return '';
  };

  return (
    <div className="blog-content-editor">
      {/* Header sticky para agregar contenido */}
      <div className="blog-content-header">
        <h3>Content</h3>
        <div className="blog-content-actions">
          <button
            type="button"
            onClick={insertTextAtEnd}
            className="blog-toolbar-button"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Paragraph
          </button>
          <button
            type="button"
            onClick={() => pickImageFile((file) => insertImageAtEnd(file))}
            className="blog-toolbar-button"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Image
          </button>
        </div>
      </div>

      {/* Contenedor del contenido con scroll */}
      <div className="blog-content-blocks" ref={blocksContainerRef}>
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
        >
          <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
            {blocks.map((block, index) => (
              <SortableBlock
                key={blockIds[index]}
                id={blockIds[index]}
                block={block}
                index={index}
                onTextChange={handleTextBlockChange}
                onRemove={handleRemoveBlock}
                resolveImageSrc={resolveImageSrc}
                imageOrderIndex={index}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
});

// Componente SortableBlock para manejar el drag and drop
const SortableBlock = ({ id, block, index, onTextChange, onRemove, resolveImageSrc, imageOrderIndex }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Solo aplicar transform si no está en estado de dragging para evitar deformación
  const style = {
    transform: isDragging ? 'none' : CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
  };

  // Función para ajustar automáticamente la altura del textarea
  const adjustTextareaHeight = (element) => {
    // Resetear altura para calcular correctamente
    element.style.height = 'auto';
    
    // Calcular la altura necesaria
    const scrollHeight = element.scrollHeight;
    const minHeight = 60; // Altura mínima en píxeles
    
    // Aplicar la altura calculada o mínima
    const newHeight = Math.max(scrollHeight, minHeight);
    element.style.height = newHeight + 'px';
    
    // Asegurar que no haya scroll
    element.style.overflow = 'hidden';
  };

  // Función para manejar cambios en el textarea
  const handleTextChange = (e) => {
    const value = e.target.value;
    onTextChange(index, value);
    
    // Ajustar altura después de un pequeño delay para asegurar que el DOM se actualice
    setTimeout(() => {
      adjustTextareaHeight(e.target);
    }, 10);
  };

  // Ajustar altura cuando el componente se monta
  React.useEffect(() => {
    if (block.type === 'text') {
      const textarea = document.querySelector(`textarea[data-block-index="${index}"]`);
      if (textarea) {
        adjustTextareaHeight(textarea);
      }
    }
  }, [block.content, index, block.type]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`content-block ${isDragging ? 'dragging' : ''}`}
      data-id={id}
    >
      {/* Drag Handle - Elegante y profesional */}
      <div
        className="content-block-drag-handle"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
      >
        <FontAwesomeIcon icon={faGripVertical} />
      </div>

      {/* Botón Remove - Posicionado arriba a la izquierda */}
      <button
        type="button"
        className="content-block-remove-button"
        onClick={() => onRemove(index, block)}
        title="Remove block"
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>

      {/* Content */}
      {block.type === 'text' ? (
        <textarea
          value={block.content || ''}
          onChange={handleTextChange}
          placeholder="Write your paragraph..."
          rows="1"
          className="blog-content-textarea"
          data-block-index={index}
        />
      ) : (
        <figure className="content-image-figure">
          {(() => {
            const src = resolveImageSrc(block, imageOrderIndex);
            return src ? <img src={src} alt={block.caption || 'Image'} /> : <div className="content-image-missing">Image</div>;
          })()}
        </figure>
      )}
    </div>
  );
};

BlogContentEditor.displayName = 'BlogContentEditor';

export default BlogContentEditor; 